// Real ground: photo-scanned soil and grass (CC0, from Poly Haven), tiled across the land
// with their own normal maps so the low sun picks out every clod, rut and tuft. On its own a
// tiled photo repeats visibly from up here, so each ground keeps its painted texture as broad
// colour variation, sampled at a different, larger scale, and the photo is multiplied into it:
// the eye reads the photo's detail and the painting's light and dark patches, and no seam lines up.
import * as THREE from 'three';

const loader = new THREE.TextureLoader();
const cache = new Map<string, THREE.Texture>();

/** One of the photo textures in public/textures: `diff` (colour) or `nor` (OpenGL normal map). */
export function photo(name: string, kind: 'diff' | 'nor'): THREE.Texture {
  const key = `${name}_${kind}`;
  let t = cache.get(key);
  if (!t) {
    t = loader.load(`${import.meta.env.BASE_URL}textures/${key}.webp`);
    t.wrapS = t.wrapT = THREE.RepeatWrapping;
    t.anisotropy = 8;
    if (kind === 'diff') t.colorSpace = THREE.SRGBColorSpace;
    cache.set(key, t);
  }
  return t;
}

/** The average colour of a canvas texture, in linear light, so the photo can be scaled to its patches. */
function meanColour(tex: THREE.CanvasTexture): THREE.Vector3 {
  const src = tex.image as HTMLCanvasElement;
  const c = document.createElement('canvas');
  c.width = c.height = 16;
  const g = c.getContext('2d')!;
  g.drawImage(src, 0, 0, 16, 16);
  const d = g.getImageData(0, 0, 16, 16).data;
  const sum = new THREE.Vector3(), c3 = new THREE.Color();
  // setRGB from sRGB lands in three's linear working space
  for (let i = 0; i < d.length; i += 4) {
    c3.setRGB(d[i]! / 255, d[i + 1]! / 255, d[i + 2]! / 255, THREE.SRGBColorSpace);
    sum.add(new THREE.Vector3(c3.r, c3.g, c3.b));
  }
  return sum.divideScalar(d.length / 4).max(new THREE.Vector3(0.01, 0.01, 0.01));
}

export interface PhotoGround {
  /** Photo texture name in public/textures. */
  name: string;
  /** Photo repeats per unit of the mesh's uv. */
  scale: number;
  /** The painted map's repeats per unit of uv (below 1 to spread it wider than it was). */
  macro?: number;
  /** How far the photo replaces the painting: 1 is the photo, tinted by the painting's patches. */
  mix?: number;
  /** Strength of the photo's normal map. */
  bump?: number;
  /** How much of the painting's hue the photo takes on, 0 to 1. */
  tint?: number;
  /** Brightness of the photo. */
  gain?: number;
  /** A second photo laid over the first in ragged, noise-edged patches. */
  second?: SecondPhoto;
}

export interface SecondPhoto {
  name: string;
  /** Photo repeats per unit of uv. */
  scale: number;
  /** How much of the ground it covers, 0 to 1 (ignored with `nearMap`). */
  cover: number;
  /** Noise features per unit of uv: higher gives smaller patches. */
  noise: number;
  /** A colour to pull its hue toward, by `tint`, and its brightness. */
  hue?: THREE.Color;
  tint?: number;
  gain?: number;
  /** Instead of patches, lay it along the map's edge: uv is tiles / tilesPerUv, the map is mapSize tiles square, and it reaches this many tiles out before giving way. */
  nearMap?: { tilesPerUv: number; mapSize: number; reach: number };
}

const NOISE = /* glsl */ `
  float gHash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
  float gNoise(vec2 p) {
    vec2 i = floor(p), f = fract(p);
    vec2 u = f * f * (3.0 - 2.0 * f);
    return mix(mix(gHash(i), gHash(i + vec2(1.0, 0.0)), u.x), mix(gHash(i + vec2(0.0, 1.0)), gHash(i + vec2(1.0, 1.0)), u.x), u.y);
  }
  float gFbm(vec2 p) {
    float v = 0.0, a = 0.5;
    for (int k = 0; k < 5; k++) { v += a * gNoise(p); p = p * 2.07 + 13.7; a *= 0.5; }
    return v;
  }
`;

/**
 * Lays a photo ground into a material whose `map` is a painted canvas texture. The canvas
 * becomes the broad colour variation; the photo supplies the detail and the normal map.
 */
export function photoGround(mat: THREE.MeshStandardMaterial | THREE.MeshLambertMaterial, o: PhotoGround): void {
  const painted = mat.map as THREE.CanvasTexture;
  const mean = meanColour(painted);
  const detail = photo(o.name, 'diff');
  const normal = photo(o.name, 'nor').clone();
  // the normal map is sampled with its own transform on the raw uv; the painted map may have one too
  normal.repeat.set(o.scale * painted.repeat.x, o.scale * painted.repeat.y);
  mat.normalMap = normal;
  mat.normalScale = new THREE.Vector2(o.bump ?? 1, o.bump ?? 1);
  const uniforms = {
    uDetail: { value: detail },
    uDetailScale: { value: o.scale },
    uMacroScale: { value: o.macro ?? 1 },
    uMacroMean: { value: mean },
    uDetailMix: { value: o.mix ?? 0.85 },
    uTint: { value: o.tint ?? 0.25 },
    uGain: { value: o.gain ?? 1 },
    uSecond: { value: o.second ? photo(o.second.name, 'diff') : detail },
    uSecondScale: { value: o.second?.scale ?? 1 },
    uSecondCover: { value: o.second?.cover ?? 0 },
    uSecondNoise: { value: o.second?.noise ?? 1 },
    uSecondHue: { value: o.second?.hue ? new THREE.Vector3(o.second.hue.r, o.second.hue.g, o.second.hue.b) : new THREE.Vector3(1, 1, 1) },
    uSecondTint: { value: o.second?.tint ?? 0 },
    uSecondGain: { value: o.second?.gain ?? 1 },
    uNearMap: { value: o.second?.nearMap ? new THREE.Vector3(o.second.nearMap.tilesPerUv, o.second.nearMap.mapSize, o.second.nearMap.reach) : new THREE.Vector3(0, 0, 0) },
  };
  mat.onBeforeCompile = s => {
    Object.assign(s.uniforms, uniforms);
    s.fragmentShader = s.fragmentShader
      .replace('#include <common>', `#include <common>
        uniform sampler2D uDetail, uSecond;
        uniform float uDetailScale, uMacroScale, uDetailMix, uTint, uGain;
        uniform float uSecondScale, uSecondCover, uSecondNoise, uSecondTint, uSecondGain;
        uniform vec3 uMacroMean, uSecondHue, uNearMap;
        ${NOISE}`)
      .replace('#include <map_fragment>', `
        #ifdef USE_MAP
          vec3 patches = texture2D(map, vMapUv * uMacroScale).rgb;
          // the patches lighten and darken the photo and lend it some of their hue
          float lum = dot(patches, vec3(0.2126, 0.7152, 0.0722)) / dot(uMacroMean, vec3(0.2126, 0.7152, 0.0722));
          // two samples of the photo, the second turned and rescaled, crossfaded by the patches, so its repeat never lines up
          vec2 p = vMapUv * uDetailScale;
          vec3 d1 = texture2D(uDetail, p).rgb;
          vec3 d2 = texture2D(uDetail, mat2(0.766, -0.643, 0.643, 0.766) * p * 0.71 + 0.37).rgb;
          vec3 detail = mix(d1, d2, smoothstep(0.85, 1.15, lum));
          // a channel the photo has none of (the leaf litter has texels with no blue) is floored: divided by
          // nothing it went to infinity, then NaN, and the bloom's blur spread one such pixel over the whole frame
          vec3 dm = max(detail / max(dot(detail, vec3(0.2126, 0.7152, 0.0722)), 0.01), vec3(0.02));
          vec3 pm = patches / max(dot(patches, vec3(0.2126, 0.7152, 0.0722)), 0.01);
          vec3 hue = mix(dm, pm, uTint) / dm;
          vec3 ground = detail * hue * lum * uGain;
          ${o.second ? `
          {
            vec3 s2 = texture2D(uSecond, vMapUv * uSecondScale).rgb;
            vec3 s2m = max(s2 / max(dot(s2, vec3(0.2126, 0.7152, 0.0722)), 0.01), vec3(0.02));
            vec3 hn = uSecondHue / max(dot(uSecondHue, vec3(0.2126, 0.7152, 0.0722)), 0.01);
            vec3 second = s2 * (mix(s2m, hn, uSecondTint) / s2m) * lum * uSecondGain;
            float n = gFbm(vMapUv * uSecondNoise);
            float mask;
            if (uNearMap.x > 0.0) {
              vec2 t = vMapUv * uNearMap.x;
              float beyond = max(max(-t.y, t.y - uNearMap.y), max(t.x - uNearMap.y, 0.0));
              mask = 1.0 - smoothstep(uNearMap.z * 0.25, uNearMap.z, beyond + (n - 0.5) * uNearMap.z * 1.2);
            } else {
              // fbm sits mostly between 0.3 and 0.7: set the threshold so about the cover share of the ground is over it
              float t = 0.72 - uSecondCover * 0.45;
              // a wide, soft edge: grass thinning out over bare earth, never a cut-out shape
              mask = smoothstep(t - 0.1, t + 0.1, n) * 0.9;
            }
            ground = mix(ground, second, mask);
          }` : ''}
          diffuseColor.rgb *= mix(patches, ground, uDetailMix);
        #endif`);
  };
  mat.customProgramCacheKey = () => `photo-ground:${o.name}:${o.second?.name ?? ''}`;
  mat.needsUpdate = true;
}

/** Blades of grass fanning up from a clump, pale so each tuft's own colour tints them; transparent between. */
function tuftTexture(): THREE.CanvasTexture {
  const n = 128;
  const c = document.createElement('canvas');
  c.width = c.height = n;
  const g = c.getContext('2d')!;
  let seed = 13;
  const r = () => ((seed = (seed * 16807) % 2147483647) / 2147483647);
  g.lineCap = 'round';
  for (let i = 0; i < 46; i++) {
    // from the clump's root at the bottom middle, curving up and out, thinning to a point
    const x0 = n / 2 + (r() - 0.5) * n * 0.3, lean = (r() - 0.5) * n * 0.9, h = n * (0.45 + r() * 0.5);
    const v = 150 + r() * 105;
    g.strokeStyle = `rgb(${v * (0.8 + r() * 0.25)},${v},${v * (0.55 + r() * 0.2)})`;
    for (let k = 0; k < 6; k++) {
      const t0 = k / 6, t1 = (k + 1) / 6;
      g.lineWidth = 3.2 * (1 - t0) + 0.4;
      g.beginPath();
      g.moveTo(x0 + lean * t0 * t0, n - h * t0);
      g.lineTo(x0 + lean * t1 * t1, n - h * t1);
      g.stroke();
    }
  }
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
}

/**
 * A grass tuft: three textured cards crossed through one root, so it reads as a clump of
 * blades from any side. Lit straight from above like the ground under it, so it never goes
 * dark against it. About a fifth of a tile tall at scale 1.
 */
export function grassTuft(): { geometry: THREE.BufferGeometry; material: THREE.MeshLambertMaterial } {
  const cards: THREE.BufferGeometry[] = [];
  for (let k = 0; k < 3; k++) {
    const p = new THREE.PlaneGeometry(0.26, 0.2);
    p.translate(0, 0.1, 0);
    p.rotateY((k / 3) * Math.PI + 0.3);
    const nor = p.attributes.normal!;
    for (let i = 0; i < nor.count; i++) nor.setXYZ(i, 0, 1, 0);
    cards.push(p);
  }
  const geometry = mergeCards(cards);
  const material = new THREE.MeshLambertMaterial({ map: tuftTexture(), alphaTest: 0.5, side: THREE.DoubleSide });
  // both faces keep the upward normal: flipped on the back, a card would face into the ground and go black
  material.onBeforeCompile = s => {
    s.fragmentShader = s.fragmentShader.replace('#include <normal_fragment_begin>',
      THREE.ShaderChunk.normal_fragment_begin.replace(/normal \*= faceDirection;/, ''));
  };
  return { geometry, material };
}

function mergeCards(cards: THREE.BufferGeometry[]): THREE.BufferGeometry {
  const pos: number[] = [], nor: number[] = [], uv: number[] = [], idx: number[] = [];
  for (const c of cards) {
    const base = pos.length / 3;
    pos.push(...(c.attributes.position!.array as Float32Array));
    nor.push(...(c.attributes.normal!.array as Float32Array));
    uv.push(...(c.attributes.uv!.array as Float32Array));
    for (const i of c.index!.array) idx.push(base + i);
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('normal', new THREE.Float32BufferAttribute(nor, 3));
  g.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
  g.setIndex(idx);
  return g;
}
